import { useState, useEffect } from 'react';
import { auth, provider } from './firebaseConfig';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    updateProfile,
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult
} from 'firebase/auth';

function AuthForm({ onSuccess }) {
    const [isRegistering, setIsRegistering] = useState(false);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    // Manejar el retorno de Google Redirect (Móviles)
    useEffect(() => {
        const checkRedirect = async () => {
            try {
                const result = await getRedirectResult(auth);
                if (result) {
                    // Usuario logueado correctamente con redirect
                    if (onSuccess) onSuccess();
                }
            } catch (error) {
                console.error("Error en redirect login:", error);
                setError("Google Error: " + error.message);
            }
        };
        checkRedirect();
    }, [onSuccess]);

    // Requisitos de contraseña
    const requirements = {
        length: password.length >= 8,
        letter: /[a-zA-Z]/.test(password),
        number: /[0-9]/.test(password),
        match: password === confirmPassword && password !== ''
    };

    const isFormValid = isRegistering
        ? (email && displayName && requirements.length && requirements.letter && requirements.number && requirements.match)
        : (email && password);

    const handleAuth = async (e) => {
        e.preventDefault();
        setError('');

        if (isRegistering && !isFormValid) return;

        setLoading(true);
        try {
            if (isRegistering) {
                const userCredential = await createUserWithEmailAndPassword(auth, email, password);
                await updateProfile(userCredential.user, {
                    displayName: displayName
                });
            } else {
                await signInWithEmailAndPassword(auth, email, password);
            }
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Auth error:", err);
            let userMsg = 'Error al ingresar. Revisa tus datos.';
            if (err.code === 'auth/email-already-in-use') userMsg = 'El correo ya está en uso.';
            if (err.code === 'auth/weak-password') userMsg = 'La contraseña es muy débil.';
            if (err.code === 'auth/invalid-email') userMsg = 'El correo no es válido.';
            if (err.code === 'auth/user-not-found' || err.code === 'auth/wrong-password') userMsg = 'Correo o contraseña incorrectos.';
            setError(userMsg);
        }
        setLoading(false);
    };

    const handleGoogle = async () => {
        try {
            // Usamos Redirect en TODOS los dispositivos por robustez y consistencia
            // Esto asegura que la persistencia funcione bien en móviles y evita popups bloqueados
            await signInWithRedirect(auth, provider);
        } catch (err) {
            console.error("Google auth error:", err);
            setError("Error Google: " + err.message);
        }
    };

    return (
        <div className="bg-neutral-900 border border-neutral-800 p-6 md:p-8 rounded-2xl md:rounded-3xl shadow-2xl max-w-sm mx-auto">
            <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6 text-center text-white">
                {isRegistering ? 'Crear Cuenta' : 'Iniciar Sesión'}
            </h2>

            <form onSubmit={handleAuth} className="space-y-4">
                {isRegistering && (
                    <div>
                        <input
                            type="text"
                            placeholder="Nombre / Alias"
                            className="w-full bg-black/50 border border-neutral-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-500 transition-all"
                            value={displayName}
                            onChange={(e) => setDisplayName(e.target.value)}
                            required
                        />
                    </div>
                )}
                <div>
                    <input
                        type="email"
                        placeholder="Correo Electrónico"
                        className="w-full bg-black/50 border border-neutral-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-500 transition-all"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                    />
                </div>
                <div>
                    <input
                        type="password"
                        placeholder="Contraseña"
                        className="w-full bg-black/50 border border-neutral-700 rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-500 transition-all"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                    />
                </div>

                {isRegistering && (
                    <>
                        <div>
                            <input
                                type="password"
                                placeholder="Repetir Contraseña"
                                className={`w-full bg-black/50 border ${requirements.match && password !== '' ? 'border-green-500/50' : 'border-neutral-700'} rounded-xl px-4 py-3 text-sm text-white focus:outline-none focus:border-yellow-500 transition-all`}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                            />
                        </div>

                        <div className="bg-black/30 p-4 rounded-xl space-y-2 border border-neutral-800">
                            <p className="text-[10px] text-gray-400 uppercase tracking-widest font-bold mb-1">Requisitos de Seguridad</p>
                            <div className="flex items-center gap-2 text-xs">
                                <span>{requirements.length ? '✅' : '❌'}</span>
                                <span className={requirements.length ? 'text-green-400' : 'text-gray-500'}>Mínimo 8 caracteres</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span>{requirements.letter ? '✅' : '❌'}</span>
                                <span className={requirements.letter ? 'text-green-400' : 'text-gray-500'}>Al menos una letra</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span>{requirements.number ? '✅' : '❌'}</span>
                                <span className={requirements.number ? 'text-green-400' : 'text-gray-500'}>Al menos un número</span>
                            </div>
                            <div className="flex items-center gap-2 text-xs">
                                <span>{requirements.match ? '✅' : '❌'}</span>
                                <span className={requirements.match ? 'text-green-400' : 'text-gray-500'}>Contraseñas coinciden</span>
                            </div>
                        </div>
                    </>
                )}

                {error && (
                    <div className="bg-red-500/10 border border-red-500/20 p-3 rounded-xl">
                        <p className="text-red-500 text-[10px] md:text-xs text-center leading-tight">{error}</p>
                    </div>
                )}

                <button
                    type="submit"
                    disabled={loading || !isFormValid}
                    className="w-full bg-yellow-600 hover:bg-yellow-500 text-black font-black py-4 rounded-xl transition-all shadow-lg hover:shadow-yellow-500/20 disabled:opacity-20 disabled:grayscale text-sm uppercase tracking-widest mt-2"
                >
                    {loading ? 'Procesando...' : (isRegistering ? 'Crear Cuenta Ahora' : 'Ingresar')}
                </button>
            </form>

            <div className="mt-6 flex items-center gap-4 text-gray-500">
                <div className="flex-1 h-px bg-neutral-800"></div>
                <span className="text-xs uppercase tracking-widest">o</span>
                <div className="flex-1 h-px bg-neutral-800"></div>
            </div>

            <button
                onClick={handleGoogle}
                className="w-full mt-6 bg-white text-black font-bold py-3 rounded-xl hover:bg-gray-200 transition-all flex items-center justify-center gap-3 shadow-xl"
            >
                <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                Continuar con Google
            </button>

            <p className="mt-8 text-center text-sm text-gray-400">
                {isRegistering ? '¿Ya tienes cuenta?' : '¿No tienes cuenta?'}
                <button
                    onClick={() => setIsRegistering(!isRegistering)}
                    className="ml-2 text-yellow-500 font-bold hover:underline"
                >
                    {isRegistering ? 'Inicia Sesión' : 'Regístrate aquí'}
                </button>
            </p>
        </div>
    );
}

export default AuthForm;
